import type { ReactElement } from 'react';
import { useCallback, useEffectEvent, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface UseFilePickerOptions {
  /** Called with the chosen file once the native chooser resolves. */
  onFileSelected: (file: File) => void;
  /**
   * Called when the chooser is dismissed without a pick, on browsers that fire
   * the `cancel` event on file inputs (Chrome 113+, Safari 16.4+, Firefox 91+).
   */
  onCancel?: () => void;
  /** Forwarded to the input's `accept` attribute. */
  accept?: string;
}

export interface UseFilePickerResult {
  /**
   * A persistent hidden `<input type='file'>`. Render it once anywhere in the
   * consumer's tree — it is inert and invisible.
   */
  filePickerInput: ReactElement;
  /**
   * Opens the native file chooser. MUST be called synchronously inside a user
   * gesture (a click handler) — browsers refuse pickers outside transient
   * user activation.
   */
  openFilePicker: () => void;
}

/**
 * Headless file-picker primitive: a persistent hidden file input plus an
 * imperative `openFilePicker()`.
 *
 * Why a PERSISTENT, framework-rendered input rather than a
 * `document.createElement('input')` created and `.click()`ed inside the
 * gesture: a field report (Chrome, macOS) showed the dynamic approach
 * silently failing — the handler ran, the hidden input was verifiably in the
 * document, `.click()` was invoked, yet no chooser opened, no `change` or
 * `cancel` ever fired, and Chrome printed no user-activation warning.
 * Playwright/Chromium testing masked this because CDP intercepts file
 * choosers at the protocol level (the *intent* registers even when the real
 * dialog would never be painted). The persistent-input pattern is the one
 * proven in real browsers by `FileDropZone` in this same directory, so this
 * hook extracts it for consumers that need a picker without the drop-zone UI.
 *
 * The input stays mounted for the consumer's lifetime, so the picked file's
 * `change` event can never be lost to the input being GC'd or unmounted while
 * the (page-blocking) native dialog is up.
 */
export function useFilePicker({
  onFileSelected,
  onCancel,
  accept,
}: UseFilePickerOptions): UseFilePickerResult {
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Effect Events always see the latest consumer callbacks, so identity churn
  // never rebinds the input's listeners (and never invalidates
  // `filePickerInput`'s memo). They are only ever called from the native
  // listeners attached in the commit-phase ref callback below.
  const handleChange = useEffectEvent((e: Event) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      onFileSelected(file);
    }
    // Reset so choosing the same file again still fires `change`.
    input.value = '';
  });
  const handleCancel = useEffectEvent(() => {
    onCancel?.();
  });

  // React has no typed `onCancel` for inputs; attach both native listeners via
  // a ref callback (React 19 cleanup-returning form).
  const attachInput = useCallback((el: HTMLInputElement | null) => {
    inputRef.current = el;
    if (!el) {
      return;
    }
    el.addEventListener('change', handleChange);
    el.addEventListener('cancel', handleCancel);
    return () => {
      el.removeEventListener('change', handleChange);
      el.removeEventListener('cancel', handleCancel);
      inputRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleChange/handleCancel are Effect Events (non-reactive, must be omitted from deps); eslint-plugin-react-hooks < 6 does not yet recognise useEffectEvent
  }, []);

  const openFilePicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const filePickerInput = useMemo(
    () =>
      createPortal(
        <input
          ref={attachInput}
          type='file'
          className='hidden'
          accept={accept}
          aria-hidden='true'
          tabIndex={-1}
        />,
        document.body
      ),
    [attachInput, accept]
  );

  return { filePickerInput, openFilePicker };
}
